import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Paid Search That Captures High-Intent Demand",
  seoDescription: "Strategic search advertising that connects your business with customers actively looking for what you offer. Efficient acquisition at scale through intent-based keyword strategy.",
  canonicalUrl: "https://xperfomax.com/paid-search",
  category: "services",
  categoryLabel: "Services / Paid Search",
  headline: "Capture demand at the moment of intent.",
  subheadline: "Strategic search advertising that connects your business with customers actively looking for what you offer.",
  heroSecondaryText: "We build and optimize paid search programs that deliver efficient acquisition at scale.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why most companies struggle with paid search.",
  problems: [
    "Over-reliance on automation without understanding what it optimizes.",
    "Vanity metrics obsession. Click-through rates over business outcomes.",
    "Bloated account structures that make optimization impossible.",
    "Campaigns run in isolation from landing pages and customer journey.",
  ],
  systemTitle: "Our Paid Search System",
  systemDescription: "Intent understanding, strategic planning, systematic execution, and continuous measurement. Every element connects to drive efficient acquisition.",
  services: [
    { name: "Intent-Based Keyword Strategy", icon: "Search", components: ["Search behavior mapping", "Competitive keyword analysis", "Intent-to-conversion alignment"], outcome: "Keywords that drive revenue, not just traffic." },
    { name: "Landing Page Alignment", icon: "Target", components: ["Message match optimization", "Quality score improvement", "Conversion rate optimization"], outcome: "Ads and destinations that work as one system." },
    { name: "Smart Bidding Optimization", icon: "TrendingUp", components: ["Machine learning bid strategies", "Performance target control", "Budget efficiency maximization"], outcome: "Automated bidding with strategic oversight." },
    { name: "Search Query Management", icon: "SlidersHorizontal", components: ["Match type refinement", "Negative keyword optimization", "Relevance improvement"], outcome: "Reduced wasted spend and improved relevance." },
    { name: "Conversion Tracking Architecture", icon: "Layers", components: ["Multi-touchpoint attribution", "Proper conversion setup", "Value-based measurement"], outcome: "True campaign value visibility across touchpoints." },
    { name: "Competitor Intelligence", icon: "Crosshair", components: ["Auction dynamics monitoring", "Competitive positioning", "Market share analysis"], outcome: "Strategic awareness of the competitive landscape." },
  ],
  systemShift: {
    title: "The search shift.",
    beforeTitle: "Before: Random Campaign Management",
    afterTitle: "After: Systematic Demand Capture",
    items: [
      { label: "Keyword Strategy", before: "Broad keywords chasing volume, not intent", after: "Intent-mapped keywords aligned to buyer journey" },
      { label: "Bidding Approach", before: "Set-and-forget manual bids or blind automation", after: "Smart bidding with strategic targets and oversight" },
      { label: "Account Structure", before: "Bloated campaigns impossible to optimize", after: "Clean architecture enabling systematic improvement" },
      { label: "Measurement", before: "Click-through rates as success metrics", after: "Revenue attribution and business outcomes" },
      { label: "Landing Experience", before: "Campaigns disconnected from destination pages", after: "Unified message match driving quality and conversion" },
    ],
  },
  engagementShape: {
    title: "How paid search engagement unfolds.",
    description: "No timelines. No deliverables lists. Structure that adapts to your business context.",
  },
  processTitle: "From audit to scale.",
  processSteps: [
    { number: "01", title: "Audit and Discovery", description: "Complete account analysis covering structure, bidding, keywords, and conversion tracking to identify gaps and quick wins." },
    { number: "02", title: "Strategy Development", description: "Intent-based keyword mapping, audience segmentation, and budget allocation tied to business objectives." },
    { number: "03", title: "Campaign Architecture", description: "Structured account setup with proper naming conventions, ad group themes, and landing page alignment." },
    { number: "04", title: "Launch and Optimize", description: "Phased rollout with continuous bid management, quality score improvement, and search query refinement." },
    { number: "05", title: "Scale and Expand", description: "Systematic growth through new keyword territories, audience expansion, and cross-channel integration." },
  ],
  technologyCategories: [
    { title: "Platforms", icon: "BarChart3", items: ["Google Ads", "Microsoft Ads", "SA360"] },
    { title: "Analytics", icon: "LineChart", items: ["Google Analytics 4", "Looker Studio", "Google Tag Manager"] },
    { title: "Research", icon: "Search", items: ["SEMrush", "SpyFu", "Google Keyword Planner"] },
    { title: "Optimization", icon: "Settings", items: ["Optmyzr", "Adalysis", "Scripts"] },
  ],
  techTitle: "Paid Search Tools",
  techDescription: "Industry-standard platforms and tools, applied with strategic intent.",
  qualification: {
    forYou: [
      "You have products or services people actively search for",
      "You're ready to invest in systematic, long-term performance improvement",
      "You have clear conversion events and ability to track value",
      "You're seeking efficient, scalable customer acquisition",
    ],
    notForYou: [
      "You expect overnight results without testing and learning",
      "Your budget cannot sustain meaningful data collection",
      "You're not willing to invest in landing page improvements",
      "You want to manage campaigns by gut feel, not data",
    ],
  },
  faqs: [
    { question: "What budget is required to see meaningful results from paid search?", answer: "There is no universal minimum, but sufficient budget depends on your industry, competition, and target geography. We assess feasibility during strategy development and recommend realistic investment levels based on opportunity size." },
    { question: "How long does it take to see performance improvements?", answer: "Initial optimizations typically show impact within the first few weeks. However, building a mature, efficient account that performs at scale usually requires three to six months of continuous refinement." },
    { question: "How do you handle attribution across channels?", answer: "We implement proper conversion tracking architecture that accounts for cross-channel influence. Reporting includes both last-click and assisted conversion data to provide a complete picture of paid search contribution." },
    { question: "Will we have access to our accounts and data?", answer: "Full account ownership and access remain with you at all times. We operate within your accounts and provide complete transparency into all changes, spend, and performance data." },
    { question: "How does paid search integrate with other marketing channels?", answer: "Paid search works best as part of a connected growth system. We coordinate with SEO for keyword coverage, with CRO for landing page optimization, and with analytics for unified measurement." },
    { question: "What reporting cadence can we expect?", answer: "Weekly performance updates are standard, with monthly strategic reviews covering progress, insights, and recommendations. Custom dashboards provide real-time access to key metrics." },
  ],
  finalCtaTitle: "Capture the demand that's already there.",
  finalCtaDescription: "Your customers are searching. The question is whether they find you or your competitors.",
  finalCtaButtonText: "Start a serious conversation",
};

const PaidSearch = () => {
  const { content } = usePageContent("paid-search", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      heroSecondaryText={content.heroSecondaryText}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      systemShift={content.systemShift}
      engagementShape={content.engagementShape}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default PaidSearch;
